import classNames from 'classnames';
import { autobind } from 'core-decorators';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { _ } from '/@/locale';
import { isMobile } from '/@/utils';

import styles from './styles.module.css';

@autobind
export default class PlantingDatesTable extends Component {
    static propTypes = {
        plantingDate: PropTypes.string.isRequired,
        products: PropTypes.array.isRequired,
        showDensity: PropTypes.bool,
        children: PropTypes.func.isRequired,
    };

    static contextTypes = {
        units: PropTypes.object,
    };

    state = this.parsePlantingDates(this.props);

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState(this.parsePlantingDates(nextProps, this.state));
    }

    render() {
        return this.props.children({
            renderPlantingDatesHeader: this.renderHeader,
            renderPlantingDatesRow: this.renderRow,
            renderPlantingDatesReferences: this.renderReferences,
        });
    }

    renderHeader() {
        const plantingMonths = this.getPlantingMonths();
        return (
            <div className={styles.header}>
                {this.state.shouldPaginate && this.renderPagination()}
                {!this.state.smallScreen && (
                    <div className={styles.months}>
                        {plantingMonths.map((plantingMonth) => {
                            const cellClassNames = classNames(
                                styles.cell,
                                styles[`range-${plantingMonth.range}`],
                                this.state.showDensity && styles.showDensity
                            );
                            return (
                                <div key={plantingMonth.month} className={cellClassNames}>
                                    {this.getMonthName(plantingMonth.month, { compact: plantingMonth.range <= 2 })}
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={styles.weeks}>
                    <div className={styles.label}>
                        {this.state.smallScreen
                            ? _('Semana de {{month}}', { month: this.getMonthName(plantingMonths?.[0]?.month) })
                            : _('Semana')}
                    </div>
                    {this.getPlantingDates().map((plantingDate) => this.renderCell(plantingDate, plantingDate.week))}
                </div>
            </div>
        );
    }

    renderRow(product) {
        return (
            <div className={styles.row}>
                {this.getPlantingDates().map((plantingDate) =>
                    this.renderCell(plantingDate, this.renderValue(product, plantingDate.month, plantingDate.week))
                )}
            </div>
        );
    }

    renderCell(plantingDate, children) {
        const cellClassNames = classNames(
            styles.cell,
            plantingDate.selected && styles.selected,
            this.state.showDensity && styles.showDensity
        );
        return (
            <div key={`${plantingDate.month}-${plantingDate.week}`} className={cellClassNames}>
                {children}
            </div>
        );
    }

    renderValue(product, month, week) {
        const plantingDate = product.plantingDates.find(
            (plantingDate) => plantingDate.month === month && plantingDate.week === week
        );
        if (plantingDate) {
            const showDensity = this.state.showDensity && Boolean(plantingDate.density);
            return (
                <div className={classNames(styles.value, styles[plantingDate.value], showDensity && styles.density)}>
                    {showDensity && this.context.units.toDensity(plantingDate.density, 'soy').localeValue}
                </div>
            );
        }
    }

    renderPagination() {
        return (
            <div className={classNames(styles.nav, this.state.smallScreen ? styles.cell : styles.overlay)}>
                <i
                    className={classNames('material-icons', this.state.offset === 0 && styles.disabled)}
                    onClick={this.handlePrev}
                >
                    chevron_left
                </i>
                <i
                    className={classNames(
                        'material-icons',
                        this.state.offset === this.state.maxOffset && styles.disabled
                    )}
                    onClick={this.handleNext}
                >
                    chevron_right
                </i>
            </div>
        );
    }

    renderReferences() {
        const { brand } = this.props.products?.[0] || {};
        const showCropSpacing = this.state.showDensity && this.context.units.soyDensity === 'm' && brand.cropSpacing;

        return (
            <div className={styles.references}>
                <div className={styles.referencesTitle}>{_('Referencias')}</div>
                <div>
                    {_('Color de las semanas')}: <span className={classNames(styles.dot, styles.G)} /> {_('Óptimo')}
                    <span className={classNames(styles.dot, styles.Y)} /> {_('Bueno')}
                </div>
                {this.state.showDensity && (
                    <div>
                        {_('Densidad recomendada en')}: {this.context.units.getUnit('density', 'soy')}
                    </div>
                )}
                {showCropSpacing && (
                    <div>{_('Espaciamiento de {{cropSpacing}}m entre líneas', { cropSpacing: brand.cropSpacing })}</div>
                )}
            </div>
        );
    }

    getPlantingDates() {
        return this.state.plantingDates.slice(this.state.offset, this.state.offset + this.state.pageSize);
    }

    getPlantingMonths() {
        const plantingDates = this.getPlantingDates();
        const months = [...new Set(plantingDates.map((plantingDate) => plantingDate.month))];
        return months.map((month) => {
            const plantingDatesByMonth = plantingDates.filter((plantingWeek) => plantingWeek.month === month);
            const weekStart = plantingDatesByMonth?.[0]?.week;
            const weekEnd = plantingDatesByMonth[plantingDatesByMonth.length - 1].week;
            return { month, range: weekEnd - weekStart + 1 };
        });
    }

    getMonthName(month, { compact = false } = {}) {
        const format = compact ? 'MMM' : 'MMMM';
        return moment()
            .month(month - 1)
            .format(format);
    }

    handleNext() {
        this.setState((prevState) => ({
            offset: Math.min(prevState.maxOffset, prevState.offset + prevState.pageSize),
        }));
    }

    handlePrev() {
        this.setState((prevState) => ({
            offset: Math.max(0, prevState.offset - prevState.pageSize),
        }));
    }

    parsePlantingDates({ products, plantingDate, showDensity }, state = {}) {
        const selectedPlantingMonth = moment(plantingDate).month() + 1;
        const selectedPlantingWeek =
            Math.floor((moment(plantingDate).date() - 1) / (moment(plantingDate).daysInMonth() / 4)) + 1;

        // Gather all unique planting dates with data.
        const plantingDateKeys = new Set();
        products.forEach((product) => {
            (product.plantingDates || []).forEach((plantingDate) => {
                const key = [plantingDate.month, plantingDate.week].join('-');
                plantingDateKeys.add(key);
            });
        });

        // Transform planting date keys ('Month-Week-Order') into objects and sort.
        const plantingDates = [...plantingDateKeys]
            .map((plantingDateKey) => {
                const [month, week, order] = plantingDateKey.split('-').map((n) => Number.parseInt(n));
                return {
                    month,
                    week,
                    order,
                    selected: month === selectedPlantingMonth && week === selectedPlantingWeek,
                };
            })
            .sort((a, b) => {
                let order = a.month - b.month;
                if (order === 0) order = a.week - b.week;
                return order;
            });

        // Besides receiving showDensity prop, check if at least 1 product actually has density value.
        showDensity =
            showDensity &&
            products.find((product) => product.plantingDates.find((plantingDate) => Boolean(plantingDate.density)));

        // Calculate pagination, which only happens in mobile for now.
        const shouldPaginate = isMobile();
        const smallScreen = shouldPaginate && (window.innerWidth === 320 || showDensity);
        let pageSize = plantingDates.length;
        let maxOffset = 0;

        if (shouldPaginate) {
            // Small screens can only render 2 weeks at a time.
            pageSize = smallScreen ? 2 : 4;

            // Add weeks to the beginning so that pages are divisible by pageSize.
            while ((plantingDates?.[0]?.week - 1) % pageSize != 0) {
                plantingDates.unshift({
                    ...plantingDates[0],
                    week: plantingDates?.[0]?.week - 1,
                    selected: false,
                });
            }

            // Add weeks to the end so that pages are divisible by pageSize.
            while (plantingDates[plantingDates.length - 1].week % pageSize != 0) {
                plantingDates.push({
                    ...plantingDates[plantingDates.length - 1],
                    week: plantingDates[plantingDates.length - 1].week + 1,
                    selected: false,
                });
            }

            maxOffset = plantingDates.length - pageSize;
        }

        // Keep previous offset value if available and within range.
        const offset = Math.min(state.offset || 0, maxOffset);

        return {
            plantingDates,
            showDensity,
            shouldPaginate,
            smallScreen,
            pageSize,
            maxOffset,
            offset,
        };
    }
}
